import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SiteLayout } from "@/features/marketing/SiteLayout";
import { useDocumentMeta } from "@/lib/document-meta";
import { fetchPublicPage, type PublicPage } from "@/lib/legal";

export const PUBLIC_INFORMATION_PATHS = [
  "/support",
  "/legal/privacy",
  "/legal/terms",
  "/legal/cookies",
  "/legal/acceptable-use",
  "/legal/subprocessors",
  "/security",
] as const;

export type PublicInformationPath = (typeof PUBLIC_INFORMATION_PATHS)[number];
export type PublicInformationKind =
  | "support"
  | "privacy"
  | "terms"
  | "cookies"
  | "acceptable-use"
  | "subprocessors"
  | "security";

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; page: PublicPage };

const PAGE_META: Record<
  PublicInformationKind,
  { title: string; description: string; path: PublicInformationPath }
> = {
  support: {
    title: "Support | SYLVIQ",
    description: "Get help with your SYLVIQ account, organization, or Accounting.",
    path: "/support",
  },
  privacy: {
    title: "Privacy Policy | SYLVIQ",
    description: "How SYLVIQ handles personal information and customer data.",
    path: "/legal/privacy",
  },
  terms: {
    title: "Terms of Service | SYLVIQ",
    description: "The terms that govern use of SYLVIQ.",
    path: "/legal/terms",
  },
  cookies: {
    title: "Cookie Policy | SYLVIQ",
    description: "How SYLVIQ uses cookies and similar technologies.",
    path: "/legal/cookies",
  },
  "acceptable-use": {
    title: "Acceptable Use Policy | SYLVIQ",
    description: "What is allowed when using SYLVIQ.",
    path: "/legal/acceptable-use",
  },
  subprocessors: {
    title: "Subprocessors | SYLVIQ",
    description: "The service providers SYLVIQ uses to operate the service.",
    path: "/legal/subprocessors",
  },
  security: {
    title: "Security | SYLVIQ",
    description: "How SYLVIQ protects accounts, data, and infrastructure.",
    path: "/security",
  },
};

function formatEffective(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function PublicInformationPage({
  kind,
}: {
  kind: PublicInformationKind;
}) {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  useDocumentMeta(PAGE_META[kind]);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    fetchPublicPage(kind).then(
      (page) => {
        if (!cancelled) setState({ status: "ready", page });
      },
      () => {
        if (!cancelled) setState({ status: "error" });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [kind]);

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
        {state.status === "loading" ? (
          <output className="text-sm text-[var(--muted-foreground)]">
            Loading…
          </output>
        ) : null}
        {state.status === "error" ? (
          <p role="alert" className="text-sm text-[var(--muted-foreground)]">
            This page is temporarily unavailable. Please try again shortly or
            email support@sylviq.io.
          </p>
        ) : null}
        {state.status === "ready" ? <PageBody page={state.page} /> : null}
        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild variant="outline">
            <Link to="/">Back to SYLVIQ</Link>
          </Button>
          <Button asChild>
            <a href="mailto:support@sylviq.io">Email support</a>
          </Button>
        </div>
      </div>
    </SiteLayout>
  );
}

function PageBody({ page }: { page: PublicPage }) {
  return (
    <>
      <p className="text-xs font-semibold tracking-widest text-[var(--action)] uppercase">
        {page.eyebrow}
      </p>
      <h1 className="mt-3 text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
        {page.title}
      </h1>
      {page.version > 0 && page.effective_at ? (
        <p className="mt-3 text-xs text-[var(--muted-foreground)]">
          Version {page.version} · Effective{" "}
          {formatEffective(page.effective_at)}
        </p>
      ) : null}
      <p className="mt-5 max-w-2xl text-base text-[var(--muted-foreground)] sm:text-lg">
        {page.description}
      </p>
      <div className="mt-10 grid gap-4">
        {page.sections.map((section) => (
          <section key={section.heading} className="surface rounded-2xl p-5">
            <h2 className="text-lg font-semibold tracking-tight">
              {section.heading}
            </h2>
            {section.paragraphs.map((paragraph) => (
              <p
                key={paragraph}
                className="mt-2 text-sm leading-6 text-[var(--muted-foreground)]"
              >
                {paragraph}
              </p>
            ))}
          </section>
        ))}
      </div>
    </>
  );
}
